import React, { useState, useEffect, forwardRef, useImperativeHandle} from "react";
import {
  Upload,
  message,
  Modal,
  Image
} from "antd";
import { PlusOutlined, BarsOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

import commonFn from "@/utils/commonFn";
import { acceptDict } from "@/utils/dict";

const CustomUploadNew = forwardRef(({
  filePaths,
  setFilePaths,
  btnDisabled,
  setBtnDisabled,
  customConfig,
}, ref) => {
  const [uploadFileList, setUploadFileList] = useState([]);
  const [pdfUrl, setPdfUrl] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [imgVisible, setImgVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [imgList, setImgList] = useState([]);
  const [pdfList, setPdfList] = useState([]);

  useImperativeHandle(ref, () => ({
    initUpdateList: (list) => {
      setBtnDisabled(true)
      getImgPdfFn(list)
      setUploadFileList(list)
      setFilePaths(list);
      setBtnDisabled(false)
      setRefreshKey(+new Date())
    },
    updateDocFn: () => {
      setRefreshKey(+new Date())
    }
  }));

  const getBase64 = (file) =>{
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    })
  };

  const deleteFn = (file, fileList) => {
    if(customConfig?.deleteOldFn && file?.id){
      let flag = false
      Modal.confirm({
        title: "Delete File",
        content: "Do you want to delete this file?",
        okText: "Confirm",
        cancelText: "Cancel",
        onOk: async() => {
          flag = await customConfig.deleteOldFn(file)
          if(flag) {
            message.success("Successfully Deleted");
            setUploadFileList(fileList)
            const list = filePaths.filter(item => item.uid !== file.uid)
            setFilePaths(list);
            getImgPdfFn(list)
          }else{
            message.warning("Failed Deleted");
          }
        },
        onCancel: () => {},
      });
      return

    }else{
      setUploadFileList(fileList)
      const list = filePaths.filter(item => item.uid !== file.uid)
      setFilePaths(list);
      getImgPdfFn(list)
    }
  }

  const isFileAccepted = (file) => {
    const defaultAccept = ["application/pdf"];

    // 使用配置的 accept 类型或默认值
    const acceptList = customConfig?.accept
      ? customConfig.accept.split(",").map(item => item.trim())
      : Object.keys(acceptDict).filter(key => defaultAccept.includes(acceptDict[key]));

    const mappedAcceptTypes = acceptList.map(type => acceptDict[type] || type);

    const isAccepted = mappedAcceptTypes.includes(file?.type || file?.contentType);

    if (!isAccepted) {
      // 找到 file.type 对应的 key，用于提示用户
      // const fallbackKey = Object.keys(acceptDict).find(key => acceptDict[key] === file.type);
      // const ext = fallbackKey?.split(".")[1]?.toUpperCase() || 'file';
      const ext = acceptList.join(',').replaceAll('.', ' ').toUpperCase() || 'file';
      message.error(`Please upload supported ${ext} file!`);
    }

    return isAccepted;
  }


  const uploadChangeFn = async({ file, fileList}) => {
    // 删除
    if(file.status === "removed"){
      deleteFn(file, fileList)
      return
    }

    if (!isFileAccepted(file)) return;

    if(fileList[fileList.length - 1]?.uid === file.uid){
      if(customConfig?.addOldFn){
        const addOldData = await customConfig.addOldFn(fileList)
        if(addOldData?.flag) {
          message.success("Upload Successfully");
          setUploadFileList([...filePaths, {...fileList[fileList.length - 1], id: addOldData?.fileData?.id, uuid: addOldData?.fileData?.uuid}])
          const list = [...filePaths, addOldData?.fileData]
          setFilePaths(list);
          getImgPdfFn(list)
        }else{
          message.warning("Failed Upload");
        }
        return
      }
      const newFileListTemp = fileList.filter((item) => filePaths.every((i) => i.uid !== item.uid));
      addUpload(newFileListTemp)
    }
  };

  const filterFileListFn = (list = [], flag) => {
    const acceptList = ["jpg", "png", "jpeg", "gif", "bmp", "webp", "svg", "ico", "tif", "tiff"]
    return list.filter(item => {
      let strList = (item?.name || item?.fileName)?.split('.')
      const str = (strList.length && strList[strList.length - 1] || '').toLowerCase()
      return flag ? acceptList.includes(str) : !acceptList.includes(str)
    })
  }

  const getImgPdfFn = (temp) => {
    setImgList(filterFileListFn(temp, true))
    setPdfList(pre => {
      const list = filterFileListFn(temp, false)
      if(!list.length){
        setPdfUrl('')
      } else if (!pdfUrl || list.every(item => item.allPath !== pdfUrl)) {
        setPdfUrl(list[0].allPath);
      }
      return list
    })
  }

  //添加时上传
  const addUpload = async (data) => {
    const temp = await Promise.all(
      data.map(async item => ({
        ...item,
        baseUrl: await getBase64(item.originFileObj),
      }))
    );
    const fileList = [...uploadFileList, ...temp]
    
    // getImgPdfFn(fileList)
    setUploadFileList(fileList)
    setBtnDisabled(true)
    const uploadedFiles = await commonFn.uploadFilesNew(temp, api.contractUploadFile);
    const list = [...filePaths, ...uploadedFiles]
    setFilePaths(list);
    setBtnDisabled(false)
    getImgPdfFn(list)
    setRefreshKey(+new Date())
  };

  const initFn = () => {
    setUploadFileList([])
    setFilePaths([])
    setImgUrl('')
    setPdfUrl('')
    setBtnDisabled(false)
    setImgVisible(false)
  }

  useEffect(()=>{
    return () =>{
      initFn()
    }
  },[])

  const isFileTypeFn = (file) => {
    const acceptList = ["jpg", "png", "jpeg", "gif", "bmp", "webp", "svg", "ico", "tif", "tiff"]
    const strList = (file.name || file.fileName)?.split('.')
    const str = (strList.length && strList[strList.length - 1] || '').toLowerCase()
    return acceptList.includes(str)
  }

  const showUploadList  = {
    // ['showPreviewIcon', 'showRemoveIcon', 'showDownloadIcon', 'removeIcon' , 'downloadIcon']
    showPreviewIcon: (file) => isFileTypeFn(file) && file.baseUrl,
  }

  const onPreview = async(file) => {
    setImgUrl(file.baseUrl)
    setImgVisible(true)
  }

  return (
    <>
      <Upload
          key={refreshKey}
          accept={customConfig?.accept || ".pdf"}
          listType="picture-card"
          fileList={uploadFileList}
          onChange={uploadChangeFn}
          beforeUpload={() => false}  // 阻止文件上传
          multiple={!customConfig?.notMultiple}
          disabled={customConfig?.disabled || btnDisabled}
          onPreview={onPreview}
          showUploadList={customConfig?.showUploadList || showUploadList}
      >
          {uploadFileList?.length >= (customConfig?.maxLength || 10000) ? null : (
            <button
                style={{
                border: 0,
                background: 'none',
                }}
                type="button"
            >
                <PlusOutlined />
                <div
                style={{
                    marginTop: 8,
                }}
                >
                { customConfig?.name || 'Upload'}
                </div>
            </button>
            )
          }
      </Upload>

      {customConfig?.status !== 'add' && imgList.length && (
        <>
          {imgList.map((item) => (
            <div style={{ display: 'inline-block', margin: '10px 10px 0 0'}}>
              <Image
                width={100}
                height={100}
                src={item.baseUrl}
                alt={item?.name || item?.fileName}
              />
            </div>
            ))
          }
        </>
      ) || null}

      {imgUrl && (
        <Image
          wrapperStyle={{
            display: 'none',
          }}
          preview={{
            visible: imgVisible,
            onVisibleChange: (visible) => setImgVisible(visible),
            afterOpenChange: (visible) => !visible && setImgUrl(''),
          }}
          src={imgUrl}
        />
      )}

      {pdfList.length && (
        <div className="mainName">
          {pdfList.map((item, index) => (
              <a
                style={{width: '100%', display: 'block', color: pdfUrl === item.allPath && '#000'}}
                key={index}
                onClick={() => setPdfUrl(item.allPath)}
              >
                <BarsOutlined /> {item?.name || item?.fileName}
              </a>
            ))
          }
        </div>
      ) || null}

      {pdfUrl && (
        <div className="PDFbox">
          <iframe
            src={pdfUrl}
            title="PDF Viewer"
            width="100%"
            style={{ height: customConfig?.pdfHeight || "50vh" }}
          />
        </div>
      )}
    </>
  );
});

export default CustomUploadNew;